import { createApi } from "@reduxjs/toolkit/query/react";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";
import { createBaseQuery } from "../../shared/utils/baseQuery";

const {
  gatewayBaseUrl,
  baseUrl,
  gatewayApiSecretKey,
  gatewayApiVersion,
  apiVersion,
  apiKey,
} = CONFIG_VARS;

export const studentsPageStudentsMutation = createApi({
  reducerPath: "studentsPageStudentsMutation",
  baseQuery: createBaseQuery(gatewayBaseUrl["1"], true), // Enable auth token
  endpoints: (builder) => ({
    resetPasswordToDefault: builder.mutation({
      query: (data) => ({
        url: `/${gatewayApiVersion}/auth/reset-password-to-default`,
        method: "POST",
        body: {
          admission_ids: data.admission_ids,
        },
        params: {
          api_secret_key: gatewayApiSecretKey,
        },
      }),
    }),
  }),
});

export const studentsPageEventsMutation = createApi({
  reducerPath: "studentsPageEventsMutation",
  baseQuery: createBaseQuery(baseUrl["1"], true), // Enable auth token
  endpoints: (builder) => ({
    getAllStudentsBySchool: builder.mutation({
      query: (data) => ({
        url: `/${apiVersion}/insights/get-all-student-by-school`,
        method: "POST",
        body: {
          school_id: data.schoolIds.schoolIds,
          is_active: true,
        },
      }),
    }),
    getEventsBySchool: builder.mutation({
      query: (data) => ({
        url: `/${apiVersion}/insights/events_by_school`,
        method: "POST",
        params: {
          school_id: data.schoolId,
          page: data.page || 1,
          limit: data.limit || 99,
        },
      }),
    }),
    getStudentsByEventIds: builder.query({
      query: (data) => ({
        url: `${apiVersion}/insights/students_by_event_ids?page=${data.page}&limit=${data.limit}`,
        method: "POST",
        body: {
          event_ids: data.event_ids,
          attempt_filter: data.attempt_filter || "ALL",
        },
      }),
      keepUnusedDataFor: 0,
      forceRefetch: ({ currentArg, previousArg }) => {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (response) => {
        if (response.error) {
          throw new Error(`${response.error.message}`);
        }
        return {
          students: response?.data?.students || [],
          totalCount: response?.data?.total_student_count || 0,
          attemptedCount: response?.data?.attempted_count || 0,
        };
      },
      transformErrorResponse: (response) => {
        return {
          status: response.status || "400",
          message:
            response.data?.message ||
            "An error occurred while fetching students",
        };
      },
    }),
    deleteStudents: builder.mutation({
      query: (data) => ({
        url: `${apiVersion}/onboarding/delete-students/admission_id`,
        method: "DELETE",
        body: data.admission_ids,
        params: {
          api_secret_key: gatewayApiSecretKey,
        },
      }),
    }),
    getSchoolDetailsById: builder.mutation({
      query: (data) => ({
        url: `${apiVersion}/insights/get-school-details`,
        method: "POST",
        body: {
          school_id: data.schoolId,
        },
      }),
    }),
    exportStudentsByEvent: builder.mutation({
      query: (data) => ({
        url: `${apiVersion}/insights/students-event/export`,
        method: "POST",
        body: {
          event_ids: data.event_ids,
          attempt_filter: data.attempt_filter,
          ...(data.isGlobal && { global_event: true }),
        },
        params: {
          api_secret_key: apiKey,
          export_format: data.export_format,
        },
      }),
    }),
    getGlobalEvents: builder.query({
      query: (data) => ({
        url: `${apiVersion}/insights/global_events`,
        method: "POST",
        params: {
          page: data.page || 1,
          limit: data.limit || 99,
          event_level: data.level,
        },
      }),
      transformResponse: (response) => {
        return response?.data || [];
      },
    }),
  }),
});

export const { useResetPasswordToDefaultMutation } =
  studentsPageStudentsMutation;
export const {
  useGetAllStudentsBySchoolMutation,
  useGetEventsBySchoolMutation,
  useGetStudentsByEventIdsQuery,
  useDeleteStudentsMutation,
  useGetSchoolDetailsByIdMutation,
  useExportStudentsByEventMutation,
  useGetGlobalEventsQuery,
} = studentsPageEventsMutation;
